import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HashRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useEffect, useState, useRef, Component, ErrorInfo, ReactNode, useCallback } from "react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import LoginPage from "./pages/LoginPage";
import HomePage from "./pages/HomePage";
import SettingsPage from "./pages/SettingsPage";
import PlanDetailsPage from "./pages/PlanDetailsPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import SubscriptionExpiredPage from "./pages/SubscriptionExpiredPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import EditProjectPage from "./pages/EditProjectPage";
import PersonDetailPage from "./pages/PersonDetailPage";
import PeoplePage from "./pages/PeoplePage";
import ReportsPage from "./pages/ReportsPage";
import NewEntryPage from "./pages/NewEntryPage";
import NewProjectPage from "./pages/NewProjectPage";
import EntryDetailPage from "./pages/EntryDetailPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import AdminPanelPage from "./pages/AdminPanelPage";
import TestNotificationsPage from "./pages/TestNotificationsPage";
import ProfileDetailScreen from "./pages/ProfileDetailScreen";
import AccountSettingsPage from "./pages/AccountSettingsPage";
import SalePage from "./pages/SalePage";
import AddCompanyPage from "./pages/AddCompanyPage";
import PurchaseService from "@/services/PurchaseService";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SubscriptionProvider, useSubscription } from "@/contexts/SubscriptionContext";
import { UserPreferencesProvider } from "@/contexts/UserPreferencesContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import NotificationPermissionSync from "@/components/NotificationPermissionSync";
import RevenueCatAuthSync from "@/components/RevenueCatAuthSync";
import { ActivityTrackingProvider } from "@/components/ActivityTrackingProvider";
import { testDirectConnectivity, resetSupabaseConnection, initializeSupabase, testConnectivityOnResume } from "@/integrations/supabase/client";
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import PaylasimPage from "./pages/PaylasimPage";
import PazaryeriPage from "./pages/PazaryeriPage";
import { useToast } from "@/hooks/use-toast";
import FaceIDService from "@/services/FaceIDService";
import CompletedProjectsPage from "./pages/CompletedProjectsPage";
import SubscriptionGuard from '@/components/SubscriptionGuard';
import { getPlatformClasses, isNativePlatform, isWeb } from "@/utils/platform";
import { useLanguage } from "@/hooks/useLanguage";
import { useTranslation } from "react-i18next";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: attempt => Math.min(1000 * 2 ** attempt, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Network error screen component
const NetworkErrorScreen = ({ onRetry }: { onRetry: () => void }) => {
  const { t } = useTranslation();
  
  return (
    <div className="flex flex-col h-screen w-screen items-center justify-center bg-background p-4">
      <div className="space-y-6 text-center max-w-md">
        <h1 className="text-xl font-bold text-foreground">{t('errors.network_error_title')}</h1>
        <p className="text-muted-foreground">
          {t('errors.network_error_description')}
        </p>
        
        <div className="flex flex-col space-y-3">
          <button 
            onClick={onRetry}
            className="px-4 py-3 bg-primary text-primary-foreground font-medium rounded-md"
          >
            {t('errors.retry_connection')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Error boundary component to catch rendering errors
class ErrorBoundary extends Component<
  { children: ReactNode, fallback?: ReactNode, t: (key: string) => string },
  { hasError: boolean, error: Error | null, errorInfo: ErrorInfo | null, key: number }
> {
  state = { hasError: false, error: null, errorInfo: null, key: 0 };
  
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    
    
    
    // Store error info for potential recovery
    this.setState({ errorInfo });
    
    // Log additional debugging info
    if (error.message && error.message.includes("Minified React error #321")) {
      
      
      
      // For hydration errors, try to recover by forcing a client-side render
      setTimeout(() => {
        
        this.setState(prevState => ({ 
          hasError: false,
          error: null,
          errorInfo: null,
          key: prevState.key + 1 
        }));
      }, 100);
    }
    else if (error.message && error.message.includes("Minified React error #300")) {
      
      
      
    }
  }
  
  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return this.props.fallback || (
        <div className="flex flex-col h-screen w-screen items-center justify-center bg-background p-4">
          <div className="space-y-6 text-center max-w-md">
            <h1 className="text-xl font-bold text-foreground">{this.props.t('errors.app_error_title')}</h1>
            <p className="text-muted-foreground">
              {this.props.t('errors.app_error_description')}
            </p>
            <button 
              onClick={() => {
                this.setState({ hasError: false, error: null, errorInfo: null });
                window.location.reload();
              }}
              className="px-4 py-3 bg-primary text-primary-foreground font-medium rounded-md"
            >
              {this.props.t('errors.refresh_page')}
            </button>
          </div>
        </div>
      );
    }
    
    // Using key to force re-render when recovering from errors
    return <div key={this.state.key}>{this.props.children}</div>;
  }
}

// Wrapper component for ErrorBoundary with translation
const ErrorBoundaryWithTranslation = ({ children, fallback }: { children: ReactNode, fallback?: ReactNode }) => {
  const { t } = useTranslation();
  return (
    <ErrorBoundary t={t} fallback={fallback}>
      {children}
    </ErrorBoundary>
  );
};

// Specific error boundary for tooltip components
class TooltipErrorBoundary extends Component<{ children: ReactNode }> {
  state = { hasError: false, errorMessage: "" };
  
  static getDerivedStateFromError(error: any) {
    // Check for specific React error codes
    const errorStr = String(error);
    let errorMessage = "Unknown tooltip error";
    
    if (errorStr.includes("error #310") || errorStr.includes("useId")) {
      errorMessage = "React context error: Tooltip provider missing or context conflict";
    } else if (errorStr.includes("error #321") || errorStr.includes("hydration")) {
      errorMessage = "Hydration mismatch: Server/client rendering difference";
    } else if (errorStr.includes("Can't find variable:") || errorStr.includes("is not defined")) {
      // Handle missing icon or component references
      errorMessage = "Missing component reference: " + errorStr;
      
      // Patch global scope for common icon errors
      if (errorStr.includes("RefreshCw") && typeof (window as any).RefreshCw === "undefined") {
        
        (window as any).RefreshCw = () => null;
      }
    } else if (error?.message) {
      errorMessage = error.message;
    }
    
    return { hasError: true, errorMessage };
  }
  
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    
    
    
    
    // Try to recover from the error by forcing a rerender after a short delay
    setTimeout(() => {
      this.setState({ hasError: false, errorMessage: "" });
    }, 500);
  }
  
  render() {
    if (this.state.hasError) {
      // Just render children without tooltips if there's an error
      return (
        <div className="tooltip-error-container">
          {this.props.children}
        </div>
      );
    }
    
    // Wrap in an error-catching try/catch as a last resort
    try {
      return this.props.children;
    } catch (error) {
      
      return <div className="tooltip-error-container">{this.props.children}</div>;
    }
  }
}

// FaceID Prompt component to check and prompt for FaceID setup
const FaceIDPrompt = () => {
  const { toast } = useToast();
  
  useEffect(() => {
    const checkFaceIDPrompt = async () => {
      // Check if we need to show FaceID prompt
      const shouldPromptFaceID = localStorage.getItem('santiyex-prompt-faceid') === 'true';
      
      if (shouldPromptFaceID && Capacitor.isNativePlatform()) {
        // Clear the flag immediately to prevent multiple prompts
        localStorage.removeItem('santiyex-prompt-faceid');
        
        // Delay the prompt slightly to ensure it appears after navigation completes
        setTimeout(async () => {
          try {
            // Ask user if they want to enable Face ID
            const wantsFaceID = window.confirm(
              "FaceID ile giriş etkinleştirilsin mi?\n\nBu, uygulamayı her açtığınızda Face ID kullanarak otomatik giriş yapmanıza olanak tanır."
            );
            
            if (wantsFaceID) {
              // First authenticate with FaceID to confirm user's identity and set it up
              const result = await FaceIDService.authenticate("Face ID'yi yapılandırmak için doğrulayın");
              
              if (result.success) {
                toast({
                  title: "FaceID Yapılandırıldı",
                  description: "Artık FaceID ile otomatik giriş yapabilirsiniz.",
                });
              } else {
                toast({
                  title: "FaceID Yapılandırılamadı",
                  description: "FaceID ile kimlik doğrulama başarısız oldu.",
                  variant: "destructive"
                });
                // Clean up any partial configuration
                FaceIDService.clearStoredCredentials();
              }
            } else {
              // User declined, clear any stored credentials
              FaceIDService.clearStoredCredentials();
            }
          } catch (error) {
            // Handle errors
            toast({
              title: "FaceID Hatası",
              description: "FaceID yapılandırılırken bir hata oluştu.",
              variant: "destructive"
            });
            // Clean up any partial configuration
            FaceIDService.clearStoredCredentials();
          }
        }, 1000);
      }
    };
    
    checkFaceIDPrompt();
  }, [toast]);
  
  return null; // This component doesn't render anything
};

// Simple AuthGuard for subscription pages
const AuthGuard: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  

  if (loading) {
    
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <div className="space-y-4 text-center">
          <div className="loader animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
          <p className="text-foreground">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    
    return <Navigate to="/login" replace />;
  }

  
  return <Outlet />;
};

const AppContent = () => {
  const { user, refreshUser } = useAuth();
  const { refreshSubscription, refreshSubscriptionOnResume } = useSubscription();
  const [networkError, setNetworkError] = useState(false);
  const networkStatus = useRef(false);
  const appState = useRef<'active' | 'background'>('active');
  const lastActiveTime = useRef(Date.now());
  const { toast } = useToast();
  useLanguage(); // Initialize language direction support

  // Add platform classes to body for CSS targeting
  useEffect(() => {
    const platformClasses = getPlatformClasses();
    document.body.className = `${document.body.className} ${platformClasses}`.trim();
    
    // Add data attributes for easier CSS targeting
    document.body.setAttribute('data-platform', isWeb() ? 'web' : 'native');
    
    return () => {
      // Cleanup platform classes
      const classesToRemove = ['platform-web', 'platform-ios', 'platform-android', 'mobile-browser', 'touch-device'];
      classesToRemove.forEach(cls => document.body.classList.remove(cls));
      document.body.removeAttribute('data-platform');
    };
  }, []);

  // App resume handler. This must stay LIGHTWEIGHT and NON-BLOCKING: the app
  // is now kept live across background→foreground (no reconnecting screen), so
  // nothing here may stall or gate rendering. We do a single quick connectivity
  // probe, kick off connection/subscription refresh in the background, and
  // quietly refetch visible data.
  const handleAppResume = useCallback(async () => {
    try {
      // Single, quick connectivity probe — no retry loop, never blocks.
      // Fall back to navigator.onLine; queries retry on their own if needed.
      let online = navigator.onLine;
      try {
        const connectivityResult = await testConnectivityOnResume();
        online = connectivityResult.success || navigator.onLine;
      } catch {
        online = navigator.onLine;
      }
      setNetworkError(!online);

      // Reset the Supabase connection in the background (non-blocking).
      resetSupabaseConnection().catch(() => {});

      // Refresh subscription in the background (fire-and-forget). Never await
      // or gate the UI on it.
      const doRefresh =
        typeof refreshSubscriptionOnResume === 'function'
          ? refreshSubscriptionOnResume
          : refreshSubscription;
      Promise.resolve()
        .then(() => doRefresh())
        .then(() => setNetworkError(false))
        .catch(() => {});

      // Quietly refresh visible data so the app shows fresh content on resume
      // without a blocking "reconnect" ceremony. Active queries refetch in the
      // background while the user keeps seeing the live screen.
      queryClient.invalidateQueries();
    } catch (error) {
      // Resume must never throw into the UI.
    }
  }, [refreshSubscription, refreshSubscriptionOnResume]);
  
  // Setup network status listener
  useEffect(() => {
    const setupNetworkListeners = async () => {
      try {
        // Only setup listeners on native platforms
        if (!isNativePlatform()) {
          return;
        }
        
        // Get initial network status
        const status = await Network.getStatus();
        networkStatus.current = status.connected;
        
        // Listen for network status changes
        Network.addListener('networkStatusChange', status => {
          
          networkStatus.current = status.connected;
          
          // If we're coming back online and app is active, check Supabase connection
          if (status.connected && appState.current === 'active') {
            
            handleAppResume();
          }
        });
      } catch (error) {
        
      }
    };
    
    setupNetworkListeners();
    
    return () => {
      // Cleanup network listeners
      Network.removeAllListeners();
    };
  }, [handleAppResume]);
  
  // Handle app state changes for iOS background/resume issues
  useEffect(() => {
    if (!isNativePlatform()) {
      return; // Only needed for native platforms
    }
    
    
    
    const handleAppStateChange = ({ isActive }: { isActive: boolean }) => {
      const now = Date.now();
      const previousState = appState.current;
      const newState = isActive ? 'active' : 'background';
      const timeSinceLastActive = now - lastActiveTime.current;
      
      
      
      // Update current state reference
      appState.current = newState;
      
      if (isActive) {
        // App is resuming from background
        if (previousState === 'background') {
          
          handleAppResume();
        }
        lastActiveTime.current = now;
      }
    };
    
    // Handle app state changes
    CapApp.addListener('appStateChange', handleAppStateChange);
    
    return () => {
      CapApp.removeAllListeners();
    };
  }, [handleAppResume]);

  // Connectivity check every 2 minutes
  useEffect(() => {
    const checkConnectivity = async () => {
      try {
        const connectivityResult = await testDirectConnectivity();
        setNetworkError(!connectivityResult.success);
      } catch (error) {
        
      }
    };
    
    // Initial check
    checkConnectivity();
    
    // Setup interval to periodically check connectivity
    const intervalId = setInterval(async () => {
      try {
        const connectivityResult = await testDirectConnectivity();
        setNetworkError(!connectivityResult.success);
      } catch (error) {
        
      }
    }, 60000); // Check every minute
    
    return () => clearInterval(intervalId);
  }, []);

  // Initialize PurchaseService when the app starts
  useEffect(() => {
    // RevenueCat initialization is now handled by RevenueCatAuthSync
    // and only happens when a Supabase user is authenticated
    // This prevents anonymous ID creation
    
    
    // Configure status bar for fullscreen
    const configureStatusBar = () => {
      // Check if StatusBar is available (Cordova plugin loaded)
      if (typeof window !== 'undefined' && window.StatusBar) {
        window.StatusBar.overlaysWebView(true);
        window.StatusBar.backgroundColorByHexString('#0A0E1A'); // match your dark background
        window.StatusBar.styleLightContent();
      }
    };
    
    // Call statusBar configuration
    configureStatusBar();
    
    // Set viewport height for mobile browsers
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
      
      // Force the app to take full screen height
      document.documentElement.style.height = '100%';
      document.body.style.height = '100%';
      document.getElementById('root')!.style.height = '100%';
      
      // Use CSS for preventing overscroll instead of touchmove handler
      document.documentElement.style.overscrollBehavior = 'none';
      document.body.style.overscrollBehavior = 'none';
    };

    // Set initial viewport height
    setViewportHeight();

    // Update on resize and orientation change
    window.addEventListener('resize', setViewportHeight);
    window.addEventListener('orientationchange', setViewportHeight);
    
    return () => {
      window.removeEventListener('resize', setViewportHeight);
      window.removeEventListener('orientationchange', setViewportHeight);
    };
  }, []);

  // If there's a network error, show the error screen
  if (networkError) {
    return (
      <NetworkErrorScreen 
        onRetry={async () => {
          try {
            const connectivityResult = await testDirectConnectivity();
            setNetworkError(!connectivityResult.success);
          } catch (error) {
            
          }
        }} 
      />
    );
  }

  // AppContent now just returns the routing structure - no duplicate Routes
  return (
    <div className="app-container">
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/waiting" element={<Navigate to="/" replace />} />
        
        {/* Pazaryeri - completely public route, no auth required */}
        <Route path="/pazaryeri" element={<PazaryeriPage />} />
        
        {/* Subscription-related routes - require auth but not subscription */}
        <Route element={<AuthGuard />}>
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/subscription-expired" element={<SubscriptionExpiredPage />} />
        </Route>
        
        {/* Protected routes - require authentication and subscription */}
        <Route element={<ProtectedRoute />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/plan-details" element={<PlanDetailsPage />} />
          <Route path="/project/:id" element={<ProjectDetailPage />} />
          <Route path="/project/:id/edit" element={<EditProjectPage />} />
          <Route path="/project/:id/person/:personId" element={<PersonDetailPage />} />
          <Route path="/people" element={<PeoplePage />} />
          <Route path="/person/:personId" element={<PersonDetailPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/paylasim" element={<PaylasimPage />} />
          <Route path="/new-entry" element={<NewEntryPage />} />
          <Route path="/new-project" element={<NewProjectPage />} />
          <Route path="/entry/:entryId" element={<EntryDetailPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/account-settings" element={<AccountSettingsPage />} />
          <Route path="/admin" element={<AdminPanelPage />} />
          <Route path="/test-notifications" element={<TestNotificationsPage />} />
          <Route path="/profile-details" element={<ProfileDetailScreen />} />
          <Route path="/completed-projects" element={<CompletedProjectsPage />} />
          <Route path="/salepage" element={<SalePage />} />
          <Route path="/sale/:id" element={<SalePage />} />
          <Route path="/add-company" element={<AddCompanyPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </div>
  );
};

// Add a component to initialize Supabase before the app starts
const SupabaseInitializer: React.FC<{children: ReactNode}> = ({ children }) => {
  const [initialized, setInitialized] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const initialize = async () => {
      try {
        await initializeSupabase();
        setInitialized(true);
      } catch (error) {
        
        // Continue anyway to avoid blocking app start
        setInitialized(true);
      }
    };

    initialize();
  }, []);

  if (!initialized) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <div className="space-y-4 text-center">
          <div className="loader animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
          <p className="text-foreground">{t('errors.app_starting')}</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// Specific error boundary for authentication components
class AuthErrorBoundary extends Component<
  { children: ReactNode, t: (key: string) => string }, 
  { hasError: boolean; errorCount: number; lastErrorTime: number; errorType: string }
> {
  state = { hasError: false, errorCount: 0, lastErrorTime: 0, errorType: '' };
  
  static getDerivedStateFromError(error: any) {
    const now = Date.now();
    const errorStr = String(error);
    let errorType = 'unknown';
    
    // ✅ FIX: Specifically handle React errors #300, #310, and #321
    if (errorStr.includes("Minified React error #310") || errorStr.includes("useState")) {
      errorType = 'hook-usage-error';
      
    } else if (errorStr.includes("Minified React error #321") || errorStr.includes("useContext")) {
      errorType = 'context-hook-error';
      
    } else if (errorStr.includes("Minified React error #300") || errorStr.includes("render into a container")) {
      errorType = 'render-container-error';
      
    } else if (errorStr.includes("l is not a function") || 
        errorStr.includes("is not a function") ||
        errorStr.includes("Cannot read properties") ||
        errorStr.includes("undefined")) {
      errorType = 'function-access-error';
      
    }
    
    return { 
      hasError: true, 
      errorCount: 1,
      lastErrorTime: now,
      errorType
    };
  }
  
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    
    
    // ✅ FIX: Handle specific error types with appropriate recovery strategies
    if (this.state.errorType === 'hook-usage-error') {
      // For React Error #310 (useState outside component), prevent infinite loops
      try {
        // Check if we've had this error recently to prevent loops
        const lastErrorKey = 'santiyex-last-hook-error';
        const lastErrorTime = localStorage.getItem(lastErrorKey);
        const now = Date.now();
        
        if (lastErrorTime && (now - parseInt(lastErrorTime)) < 10000) {
          // If we had this error less than 10 seconds ago, don't reload
          
          return;
        }
        
        // Store current time to prevent loops
        localStorage.setItem(lastErrorKey, now.toString());
        
        // Clear auth-related cache that might be causing the issue
        localStorage.removeItem('santiyex-user-cache');
        localStorage.removeItem('santiyex-session-expiry');
        localStorage.removeItem('santiyex-user-lock-status');
        
        
        // ✅ IMPROVEMENT: Check if error is related to OneSignal or async operations
        if (error.stack && (error.stack.includes('OneSignal') || error.stack.includes('setTimeout') || error.stack.includes('import'))) {
          
          
          
        }
        
        // Force a hard refresh after a short delay to reset React state completely
        setTimeout(() => {
          
          window.location.reload();
        }, 1500);
      } catch (cleanupError) {
        
      }
    } else if (this.state.errorType === 'context-hook-error') {
      // For hook errors, try to recover by clearing auth state
      try {
        localStorage.removeItem('santiyex-user-cache');
        localStorage.removeItem('santiyex-session-expiry');
        
      } catch (cleanupError) {
        
      }
    } else if (this.state.errorType === 'render-container-error') {
      // For render errors, ensure DOM is ready
      
    }
    
    // Prevent infinite error loops by limiting recovery attempts
    if (this.state.errorCount < 3) {
      // For hook usage errors, don't attempt recovery - just show permanent error
      if (this.state.errorType === 'hook-usage-error') {
        
        return;
      }
      
      // Try to recover by resetting after a delay for other error types
      setTimeout(() => {
        
        this.setState(prevState => ({ 
          hasError: false,
          errorCount: prevState.errorCount + 1,
          lastErrorTime: Date.now(),
          errorType: ''
        }));
      }, 1000 * this.state.errorCount); // Increasing delay: 1s, 2s, 3s
    } else {
      // Too many errors, show permanent error state
      
    }
  }
  
  render() {
    if (this.state.hasError) {
      // If we've had too many errors, show a permanent error state
      if (this.state.errorCount >= 3) {
        return (
          <div className="flex h-screen w-screen items-center justify-center bg-background">
            <div className="space-y-4 text-center max-w-md p-4">
              <h1 className="text-xl font-bold text-foreground">{this.props.t('errors.auth_error_title')}</h1>
              <p className="text-muted-foreground">
                {this.props.t('errors.auth_error_description')}
              </p>
              {/* ✅ FIX: Show error type for debugging */}
              {this.state.errorType && (
                <p className="text-xs text-muted-foreground font-mono">
                  {this.props.t('errors.error_type')}: {this.state.errorType}
                </p>
              )}
              <div className="flex flex-col space-y-2">
                <button 
                  onClick={() => {
                    // Clear any stored auth state and reload
                    localStorage.clear();
                    window.location.reload();
                  }}
                  className="px-4 py-3 bg-primary text-primary-foreground font-medium rounded-md"
                >
                  {this.props.t('errors.restart_app')}
                </button>
                <button 
                  onClick={() => {
                    // Try to force reset without clearing all localStorage
                    localStorage.removeItem('santiyex-user-cache');
                    localStorage.removeItem('santiyex-session-expiry');
                    this.setState({ hasError: false, errorCount: 0, errorType: '' });
                  }}
                  className="px-4 py-3 bg-secondary text-secondary-foreground font-medium rounded-md"
                >
                  {this.props.t('errors.clear_session_only')}
                </button>
              </div>
            </div>
          </div>
        );
      }
      
      // Show loading while attempting recovery
      return (
        <div className="flex h-screen w-screen items-center justify-center bg-background">
          <div className="space-y-4 text-center">
            <div className="loader animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto"></div>
            {/* ✅ FIX: Show specific message for React Error #310 */}
            {this.state.errorType === 'hook-usage-error' ? (
              <>
                <p className="text-foreground">{this.props.t('errors.app_fixing')}</p>
                <p className="text-sm text-muted-foreground">
                  {this.props.t('errors.react_error_detection')}
                </p>
              </>
            ) : (
              <>
                <p className="text-foreground">{this.props.t('errors.auth_fixing')}</p>
                {/* ✅ FIX: Show recovery attempt count */}
                <p className="text-sm text-muted-foreground">
                  {this.props.t('errors.attempt_count', { count: this.state.errorCount })}
                </p>
              </>
            )}
          </div>
        </div>
      );
    }
    
    return this.props.children;
  }
}

// Wrapper component for AuthErrorBoundary with translation
const AuthErrorBoundaryWithTranslation = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  return (
    <AuthErrorBoundary t={t}>
      {children}
    </AuthErrorBoundary>
  );
};

const App = () => (
  <ErrorBoundaryWithTranslation>
    <HashRouter>
      <QueryClientProvider client={queryClient}>
        {/* ✅ FIX: Wrap auth providers with AuthErrorBoundary */}
        <AuthErrorBoundaryWithTranslation>
          <AuthProvider>
            <SubscriptionProvider>
              <UserPreferencesProvider>
                <TooltipProvider>
                  <NotificationPermissionSync />
                  <RevenueCatAuthSync />
                  <AppContent />
                  <Toaster />
                  <Sonner />
                </TooltipProvider>
              </UserPreferencesProvider>
            </SubscriptionProvider>
          </AuthProvider>
        </AuthErrorBoundaryWithTranslation>
      </QueryClientProvider>
    </HashRouter>
  </ErrorBoundaryWithTranslation>
);

export default App;
